/**
 * D2X Pack Builder — a guided, multi-step, quantity-based "build your sponsorship pack"
 * experience. Unlike assets/bundle-builder.js (single-select per step), each step here
 * has a required quantity that can be filled with multiple different SKUs (e.g. 1 Clash +
 * 3 Ultra rackets to fill a 4-racket step).
 *
 * All packs are FOC (free of charge). This component adds selections to the cart at RRP
 * via the theme's standard cart-add route (mirroring bundle-builder.js's pattern), tagging
 * each line with `_pack_id`/`_pack_step` properties. Real $0 checkout pricing is applied
 * by a backend Cart Transform Function (out of scope for this theme repo).
 *
 * @module d2x-pack-builder
 */

import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, CartUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const Events = {
  add: 'd2x-pack:add',
  change: 'd2x-pack:selection-changed',
  reset: 'd2x-pack:reset',
};

const DRAWER_BREAKPOINT = 990;

/**
 * @typedef {object} PackLine
 * @property {string} variantId
 * @property {number} productId
 * @property {string} step
 * @property {string} title
 * @property {string} variantTitle
 * @property {string} image
 * @property {number} price
 * @property {number} qty
 */

/**
 * A single selectable product tile. Owns its own inline size overlay; stages selections
 * on the nearest `d2x-pack-builder-component` rather than adding to cart directly.
 */
class PackTile extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.step = this.dataset.productStep || '';
    this.productId = Number(this.dataset.productId);
    this.isSingleVariant = this.dataset.singleVariant === 'true';

    this.quickAdd = this.querySelector('.quick-add');
    this.sizesOverlay = this.refs.sizesOverlay || this.querySelector('.quick-add-sizes-overlay');
    this.addTrigger = /** @type {HTMLButtonElement | null} */ (
      this.refs.addTrigger || this.querySelector('.d2x-pack-tile__add')
    );

    this.addTrigger?.addEventListener('click', this.#onAddTrigger, { signal });

    for (const cell of this.querySelectorAll('.quick-add-size-cell')) {
      cell.addEventListener('click', this.#onSizeSelect, { signal });
    }

    // Backdrop overlay click listener
    this.quickAdd?.addEventListener(
      'click',
      (e) => {
        if (
          this.quickAdd?.classList.contains('quick-add--open') &&
          (e.target === this.quickAdd || e.target === this.sizesOverlay)
        ) {
          this.closeSizesOverlay();
        }
      },
      { signal }
    );

    // Document click listener to close when clicking outside tile
    document.addEventListener(
      'click',
      (e) => {
        if (this.quickAdd?.classList.contains('quick-add--open')) {
          if (e.target instanceof Node && !this.contains(e.target)) {
            this.closeSizesOverlay();
          }
        }
      },
      { signal }
    );

    // Mouseover listener to close when mouse leaves tile (matching product card quick-add behavior)
    document.addEventListener(
      'mouseover',
      (e) => {
        if (this.quickAdd?.classList.contains('quick-add--open')) {
          if (e.target instanceof Node && !this.contains(e.target)) {
            this.closeSizesOverlay();
          }
        }
      },
      { signal }
    );

    document.addEventListener(Events.change, this.#onSelectionChanged, { signal });
    document.addEventListener(Events.reset, this.#onSelectionChanged, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  openSizesOverlay() {
    // Close any other open quick add / tile overlays across the page
    document.querySelectorAll('.quick-add--open').forEach((el) => {
      if (el !== this.quickAdd) {
        el.classList.remove('quick-add--open');
        const overlay = el.querySelector('.quick-add-sizes-overlay');
        overlay?.classList.remove('active');
        const btn = el.querySelector('.quick-add__button');
        btn?.classList.remove('quick-add-button--close');
      }
    });

    if (this.sizesOverlay) {
      this.sizesOverlay.classList.add('active');
    }
    if (this.addTrigger) {
      this.addTrigger.classList.add('quick-add-button--close');
    }
    if (this.quickAdd) {
      this.quickAdd.classList.add('quick-add--open');
    }
  }

  closeSizesOverlay() {
    if (this.sizesOverlay) {
      this.sizesOverlay.classList.remove('active');
    }
    if (this.addTrigger) {
      this.addTrigger.classList.remove('quick-add-button--close');
    }
    if (this.quickAdd) {
      this.quickAdd.classList.remove('quick-add--open');
    }
  }

  /** @param {Event} event */
  #onAddTrigger = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.isSingleVariant) {
      this.#stage(this.dataset.variantId, '');
      return;
    }
    if (this.quickAdd?.classList.contains('quick-add--open')) {
      this.closeSizesOverlay();
    } else {
      this.openSizesOverlay();
    }
  };

  /** @param {MouseEvent} event */
  #onSizeSelect = (event) => {
    const cell = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (cell.disabled) return;
    const variantTitle = cell.dataset.variantTitle || cell.textContent?.trim() || '';
    this.#stage(cell.dataset.variantId, variantTitle, {
      inventoryQuantity: cell.dataset.inventoryQuantity,
      inventoryManagement: cell.dataset.inventoryManagement,
      inventoryPolicy: cell.dataset.inventoryPolicy,
    });
    this.closeSizesOverlay();
  };

  /**
   * @param {string | undefined} variantId
   * @param {string} variantTitle
   * @param {Record<string, any>} [customDataset]
   */
  #stage(variantId, variantTitle, customDataset = {}) {
    if (!variantId) return;
    document.dispatchEvent(
      new CustomEvent(Events.add, {
        bubbles: true,
        detail: {
          variantId,
          variantTitle,
          productId: this.productId,
          step: this.step,
          title: this.dataset.productTitle || '',
          image: this.dataset.productImage || '',
          price: Number(this.dataset.productPrice) || 0,
          inventoryQuantity: customDataset.inventoryQuantity ?? this.dataset.inventoryQuantity,
          inventoryManagement: customDataset.inventoryManagement ?? this.dataset.inventoryManagement,
          inventoryPolicy: customDataset.inventoryPolicy ?? this.dataset.inventoryPolicy,
        },
      })
    );
  }

  /**
   * A change event fires per-step (event.detail.selections is only that step's lines).
   * The same product can legitimately appear as a separate tile in more than one step
   * (e.g. duplicate collections during testing) — each tile must only react to the
   * event for *its own* step, otherwise selection state leaks across steps that happen
   * to share a product.
   * @param {CustomEvent} event
   */
  #onSelectionChanged = (event) => {
    if (event.type === Events.reset) {
      this.classList.remove('d2x-pack-tile--has-selection', 'd2x-pack-tile--limit-reached');
      if (this.refs.selectedCount) this.refs.selectedCount.textContent = '0';
      if (this.refs.addTrigger instanceof HTMLButtonElement) this.refs.addTrigger.disabled = false;
      return;
    }

    if (event.detail?.step !== this.step) return;

    /** @type {PackLine[]} */
    const lines = event.detail?.selections || [];
    const mine = lines.filter((l) => l.productId === this.productId);
    const count = mine.reduce((sum, l) => sum + l.qty, 0);

    this.classList.toggle('d2x-pack-tile--has-selection', count > 0);
    if (this.refs.selectedCount) this.refs.selectedCount.textContent = String(count);

    const mode = event.detail?.mode;
    const remaining = Number(event.detail?.remaining) || 0;
    const price = Number(this.dataset.productPrice) || 0;

    let limitReached = false;
    if (count === 0) {
      limitReached = mode === 'dollar_limit' ? remaining < price : remaining <= 0;
    }

    this.classList.toggle('d2x-pack-tile--limit-reached', limitReached);
    if (this.refs.addTrigger instanceof HTMLButtonElement) {
      this.refs.addTrigger.disabled = limitReached;
    }
  };
}

if (!customElements.get('pack-tile-component')) {
  customElements.define('pack-tile-component', PackTile);
}

/**
 * Top-level coordinator: step tabs, per-step required quantities, the running selection
 * state, the summary panel, and the final add-to-cart submission.
 */
class D2xPackBuilder extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {Map<string, PackLine[]>} */
  #selections = new Map();

  /** @type {Set<string>} */
  #visitedSteps = new Set();

  /** @type {Map<string, number>} Current 0-based page index per step. */
  #pages = new Map();

  /** @type {Map<string, string>} Error messages for line items with stock limit reached. */
  #lineErrors = new Map();

  isPackInCart = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.packHandle = this.dataset.packHandle || '';
    this.storageKey = `wilson:d2x-pack:${this.packHandle}`;

    this.tabs = [...this.querySelectorAll('.d2x-pack-builder__tab')];
    this.steps = [...this.querySelectorAll('.d2x-pack-builder__step')];
    this.summaryRows = [...this.querySelectorAll('.d2x-pack-summary__row')];
    this.paginations = [...this.querySelectorAll('.d2x-pack-builder__pagination')];
    this.stepConfig = this.#readConfig();
    this.variantInventory = this.#readVariantInventory();
    this.#populateVariantInventoryFromDOM();

    for (const step of this.stepConfig) {
      this.#selections.set(step.title, []);
    }

    for (const pagination of this.paginations) {
      const step = pagination.dataset.productStep || '';
      this.#pages.set(step, 0);
      pagination.querySelector('[data-page-prev]')?.addEventListener('click', this.#onPagePrev, { signal });
      pagination.querySelector('[data-page-next]')?.addEventListener('click', this.#onPageNext, { signal });
    }

    for (const tab of this.tabs) {
      tab.addEventListener('click', this.#onTabClick, { signal });
    }
    for (const button of this.querySelectorAll('[data-jump-step]')) {
      button.addEventListener('click', this.#onJumpStep, { signal });
    }

    this.refs.summaryHeader?.addEventListener('click', this.#toggleSummary, { signal });
    this.refs.summaryOverlay?.addEventListener('click', this.#closeSummary, { signal });
    this.refs.cta?.addEventListener('click', this.#submit, { signal });
    document.addEventListener(Events.add, this.#onAdd, { signal });
    document.addEventListener('cart:update', this.#onCartUpdate, { signal });
    window.addEventListener('resize', this.#positionDrawer, { signal });

    if (this.tabs[0]) this.#visitedSteps.add(this.tabs[0].dataset.productStep || '');

    this.#restore();
    this.#refresh();
    this.#syncCartAsync();
    this.#positionDrawer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #readConfig() {
    try {
      return JSON.parse(this.refs.config?.textContent || '{}').steps || [];
    } catch {
      return [];
    }
  }

  #readLabels() {
    try {
      return JSON.parse(this.refs.config?.textContent || '{}').labels || {};
    } catch {
      return {};
    }
  }

  #readVariantInventory() {
    try {
      return JSON.parse(this.refs.variantInventory?.textContent || '{}');
    } catch {
      return {};
    }
  }

  #populateVariantInventoryFromDOM() {
    for (const el of this.querySelectorAll('[data-variant-id]')) {
      const vId = el.getAttribute('data-variant-id');
      const qtyAttr = el.getAttribute('data-inventory-quantity');
      if (vId && qtyAttr !== null && qtyAttr !== undefined && qtyAttr !== '') {
        const parsed = Number(qtyAttr);
        if (!isNaN(parsed)) {
          const key = String(vId);
          if (!this.variantInventory[key]) {
            this.variantInventory[key] = {
              management: el.getAttribute('data-inventory-management') || 'shopify',
              policy: el.getAttribute('data-inventory-policy') || 'deny',
              quantity: parsed,
            };
          }
        }
      }
    }
  }

  /**
   * @param {string} variantId
   * @param {PackLine} [line]
   * @returns {number | null}
   */
  #getMaxInventory(variantId, line) {
    if (line && typeof line.inventoryQuantity === 'number' && !isNaN(line.inventoryQuantity)) {
      return line.inventoryQuantity;
    }
    const key = String(variantId);
    const inv =
      this.variantInventory[key] ||
      this.variantInventory[variantId] ||
      this.variantInventory[Number(variantId)];
    if (inv && inv.quantity !== null && inv.quantity !== undefined && inv.quantity !== '') {
      const parsed = Number(inv.quantity);
      if (!isNaN(parsed) && parsed >= 0) return parsed;
    }

    const el = this.querySelector(`[data-variant-id="${key}"]`);
    if (el) {
      const qtyAttr = el.getAttribute('data-inventory-quantity');
      if (qtyAttr !== null && qtyAttr !== undefined && qtyAttr !== '') {
        const parsed = Number(qtyAttr);
        if (!isNaN(parsed) && parsed >= 0) {
          this.variantInventory[key] = { quantity: parsed };
          if (line) line.inventoryQuantity = parsed;
          return parsed;
        }
      }
    }
    return null;
  }

  #stepConfigFor(step) {
    return this.stepConfig.find((s) => s.title === step);
  }

  #stepMode(step) {
    return this.#stepConfigFor(step)?.mode || 'quantity';
  }

  #requiredQty(step) {
    return this.#stepConfigFor(step)?.requiredQty || 0;
  }

  #dollarCap(step) {
    return this.#stepConfigFor(step)?.dollarCap || 0;
  }

  #selectedQty(step) {
    return (this.#selections.get(step) || []).reduce((sum, l) => sum + l.qty, 0);
  }

  #selectedAmount(step) {
    return (this.#selections.get(step) || []).reduce((sum, l) => sum + l.price * l.qty, 0);
  }

  #isStepComplete(step) {
    if (this.#stepMode(step) === 'dollar_limit') {
      return (this.#selections.get(step) || []).length > 0;
    }
    return this.#selectedQty(step) >= this.#requiredQty(step);
  }

  /** @param {MouseEvent} event */
  #onTabClick = (event) => {
    const step = /** @type {HTMLElement} */ (event.currentTarget).dataset.productStep;
    if (step) this.showStep(step);
  };

  /** @param {MouseEvent} event */
  #onJumpStep = (event) => {
    const step = /** @type {HTMLElement} */ (event.currentTarget).dataset.jumpStep;
    if (step) this.showStep(step);
    this.#closeSummary();
  };

  /** @param {string} step */
  showStep(step) {
    this.#visitedSteps.add(step);
    for (const tab of this.tabs) {
      tab.classList.toggle('d2x-pack-builder__tab--active', tab.dataset.productStep === step);
    }
    for (const panel of this.steps) {
      const match = panel.dataset.productStep === step;
      panel.toggleAttribute('hidden', !match);
    }
    const targetY = window.scrollY + this.getBoundingClientRect().top - 130;
    window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    this.#refreshTabStates();
  }

  /** @param {MouseEvent} event */
  #onPagePrev = (event) => {
    const pagination = /** @type {HTMLElement} */ (event.currentTarget).closest('.d2x-pack-builder__pagination');
    const step = /** @type {HTMLElement | null} */ (pagination)?.dataset.productStep;
    if (!step) return;
    this.#showPage(step, (this.#pages.get(step) || 0) - 1);
  };

  /** @param {MouseEvent} event */
  #onPageNext = (event) => {
    const pagination = /** @type {HTMLElement} */ (event.currentTarget).closest('.d2x-pack-builder__pagination');
    const step = /** @type {HTMLElement | null} */ (pagination)?.dataset.productStep;
    if (!step) return;
    this.#showPage(step, (this.#pages.get(step) || 0) + 1);
  };

  /**
   * @param {string} step
   * @param {number} pageIndex
   */
  #showPage(step, pageIndex) {
    const panel = this.steps.find((p) => p.dataset.productStep === step);
    const pagination = this.paginations.find((p) => p.dataset.productStep === step);
    if (!panel || !pagination) return;

    const pageCount = Number(pagination.dataset.pageCount) || 1;
    const page = Math.max(0, Math.min(pageIndex, pageCount - 1));
    this.#pages.set(step, page);

    for (const item of panel.querySelectorAll('.d2x-pack-builder__grid > li[data-page]')) {
      item.toggleAttribute('hidden', Number(/** @type {HTMLElement} */ (item).dataset.page) !== page);
    }

    const current = pagination.querySelector('[data-page-current]');
    if (current) current.textContent = String(page + 1);

    const prevBtn = pagination.querySelector('[data-page-prev]');
    if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = page <= 0;

    const nextBtn = pagination.querySelector('[data-page-next]');
    if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = page >= pageCount - 1;

    panel.querySelector('.d2x-pack-builder__grid')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /** @param {CustomEvent} event */
  #onAdd = (event) => {
    const detail = event.detail;
    if (!detail || !this.#selections.has(detail.step)) return;

    const lines = this.#selections.get(detail.step) || [];
    const mode = this.#stepMode(detail.step);

    if (mode === 'dollar_limit') {
      const cap = this.#dollarCap(detail.step);
      const spent = this.#selectedAmount(detail.step);
      if (spent + (detail.price || 0) > cap) return;
    } else {
      const currentQty = lines.reduce((sum, l) => sum + l.qty, 0);
      const required = this.#requiredQty(detail.step);
      if (currentQty >= required) return;
    }

    const variantId = String(detail.variantId);
    let invQty = (detail.inventoryQuantity !== undefined && detail.inventoryQuantity !== null && detail.inventoryQuantity !== '')
      ? Number(detail.inventoryQuantity)
      : null;
    if (isNaN(invQty)) invQty = null;

    if (invQty !== null && !this.variantInventory[variantId]) {
      this.variantInventory[variantId] = {
        management: detail.inventoryManagement || 'shopify',
        policy: detail.inventoryPolicy || 'deny',
        quantity: invQty,
      };
    }

    const maxInventory = invQty ?? this.#getMaxInventory(variantId);
    const existing = lines.find((l) => l.variantId === variantId);
    const targetQty = (existing ? existing.qty : 0) + 1;

    if (maxInventory !== null && targetQty > maxInventory) {
      const labels = this.#readLabels();
      const template = labels.maxQuantityError || 'This product has only {{ quantity }} quantity.';
      const msg = template.replace(/\{\{\s*quantity\s*\}\}/g, String(maxInventory));
      this.#lineErrors.set(variantId, msg);
      this.#refresh();
      return;
    }

    this.#lineErrors.delete(variantId);

    if (this.isPackInCart) {
      this.#addCartItem(detail);
      return;
    }

    if (existing) {
      existing.qty += 1;
      if (maxInventory !== null) existing.inventoryQuantity = maxInventory;
    } else {
      lines.push({
        variantId,
        productId: detail.productId,
        step: detail.step,
        title: detail.title,
        variantTitle: detail.variantTitle || '',
        image: detail.image,
        price: detail.price,
        qty: 1,
        inventoryQuantity: maxInventory ?? undefined,
      });
    }

    this.#selections.set(detail.step, lines);
    this.#persist();
    this.#refresh();
  };

  /**
   * @param {string} step
   * @param {string} variantId
   * @param {number} delta
   */
  #changeQty(step, variantId, delta) {
    const lines = this.#selections.get(step) || [];
    const line = lines.find((l) => l.variantId === variantId);
    if (!line) return;

    if (this.isPackInCart) {
      const newQty = Math.max(0, line.qty + delta);
      this.#updateCartItem(line.key, newQty);
      return;
    }

    if (delta > 0) {
      const maxInventory = this.#getMaxInventory(variantId, line);
      if (maxInventory !== null && line.qty + delta > maxInventory) {
        const labels = this.#readLabels();
        const template = labels.maxQuantityError || 'This product has only {{ quantity }} quantity.';
        const msg = template.replace(/\{\{\s*quantity\s*\}\}/g, String(maxInventory));
        this.#lineErrors.set(variantId, msg);
        this.#refresh();
        return;
      }

      if (this.#stepMode(step) === 'dollar_limit') {
        const cap = this.#dollarCap(step);
        const spent = this.#selectedAmount(step);
        if (spent + line.price > cap) return;
      } else {
        const otherQty = lines.filter((l) => l !== line).reduce((sum, l) => sum + l.qty, 0);
        if (otherQty + line.qty >= this.#requiredQty(step)) return;
      }

      this.#lineErrors.delete(variantId);
    } else if (delta < 0) {
      this.#lineErrors.delete(variantId);
    }

    line.qty = Math.max(1, line.qty + delta);
    this.#selections.set(step, lines);
    this.#persist();
    this.#refresh();
  }

  /**
   * @param {string} step
   * @param {string} variantId
   */
  #removeLine(step, variantId) {
    this.#lineErrors.delete(variantId);
    const lines = this.#selections.get(step) || [];
    const line = lines.find((l) => l.variantId === variantId);
    if (!line) return;

    if (this.isPackInCart) {
      this.#updateCartItem(line.key, 0);
      return;
    }

    const remainingLines = lines.filter((l) => l.variantId !== variantId);
    this.#selections.set(step, remainingLines);
    this.#persist();
    this.#refresh();
  }

  #persist() {
    try {
      const plain = Object.fromEntries(this.#selections);
      localStorage.setItem(this.storageKey, JSON.stringify(plain));
    } catch {
      /* storage unavailable — selections simply won't persist across reloads */
    }
  }

  #restore() {
    try {
      const raw = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
      for (const [step, lines] of Object.entries(raw)) {
        if (this.#selections.has(step) && Array.isArray(lines)) {
          this.#selections.set(step, lines);
        }
      }
    } catch {
      /* ignore malformed storage */
    }
  }

  #refreshTabStates() {
    for (const tab of this.tabs) {
      const step = tab.dataset.productStep || '';
      const lines = this.#selections.get(step) || [];
      const qty = lines.reduce((sum, l) => sum + l.qty, 0);
      const hasItems = qty > 0;
      const complete = this.#isStepComplete(step);
      const visited = this.#visitedSteps.has(step);
      const active = tab.classList.contains('d2x-pack-builder__tab--active');

      tab.classList.toggle('d2x-pack-builder__tab--complete', complete);
      tab.classList.toggle('d2x-pack-builder__tab--incomplete', visited && !complete && !active);
      tab.classList.toggle('d2x-pack-builder__tab--has-items', hasItems);
      tab.classList.toggle('d2x-pack-builder__tab--selected', hasItems);
      tab.classList.toggle('d2x-pack-builder__tab--has-selections', hasItems);

      const stepPanel = this.steps.find((p) => p.dataset.productStep === step);
      if (stepPanel) {
        stepPanel.classList.toggle('d2x-pack-builder__step--complete', complete && this.#stepMode(step) !== 'dollar_limit');
      }
    }
  }

  #refresh = () => {
    const labels = this.#readLabels();
    let completeSteps = 0;
    let totalPrice = 0;
    let totalQty = 0;

    this.classList.toggle('d2x-pack-builder--in-cart', this.isPackInCart);
    if (this.refs.inCartNotice instanceof HTMLElement) {
      this.refs.inCartNotice.style.display = this.isPackInCart ? 'block' : 'none';
    }

    for (const step of this.stepConfig) {
      const lines = this.#selections.get(step.title) || [];
      const qty = lines.reduce((sum, l) => sum + l.qty, 0);
      const amount = lines.reduce((sum, l) => sum + l.price * l.qty, 0);
      const complete = this.#isStepComplete(step.title);
      if (complete) completeSteps += 1;
      totalPrice += amount;
      totalQty += qty;

      const stepPanel = this.steps.find((p) => p.dataset.productStep === step.title);
      const countLabel = stepPanel?.querySelector('.d2x-pack-builder__step-count');
      const { format, currency } = this.#moneyConfig();
      let remaining;

      if (step.mode === 'dollar_limit') {
        remaining = Math.max(0, step.dollarCap - amount);
        if (countLabel) {
          countLabel.textContent = `${formatMoney(amount, format, currency)} / ${formatMoney(step.dollarCap, format, currency)} ${labels.used || 'used'}`;
        }
      } else {
        remaining = Math.max(0, step.requiredQty - qty);
        if (countLabel) {
          countLabel.classList.toggle('d2x-pack-builder__step-count--complete', complete);
          const textEl = countLabel.querySelector('.d2x-pack-builder__step-count-text');
          const itemsSelected = labels.itemsSelected || 'items selected';
          if (textEl) {
            textEl.textContent = complete ? `${qty} ${itemsSelected}` : `${labels.pick || 'Pick'} ${step.requiredQty} - ${qty} ${itemsSelected}`;
          }
        }
      }

      this.#renderSummaryRow(step, lines);

      document.dispatchEvent(
        new CustomEvent(Events.change, {
          detail: { step: step.title, selections: lines, mode: step.mode, remaining, inCart: this.isPackInCart },
        })
      );
    }

    this.#refreshTabStates();

    const totalRequiredQty = Number(this.dataset.totalRequiredQty) || 0;
    if (this.refs.progressCount) {
      this.refs.progressCount.textContent = totalRequiredQty > 0 ? `${totalQty}/${totalRequiredQty}` : `${totalQty}`;
    }
    if (this.refs.progressFill) {
      const pct = totalRequiredQty > 0 ? Math.min(1, totalQty / totalRequiredQty) * 100 : 0;
      this.refs.progressFill.style.width = `${pct}%`;
    }

    const total = this.stepConfig.length;
    if (this.refs.summaryCount) {
      const itemsLabel = labels.items || 'items';
      this.refs.summaryCount.textContent = totalRequiredQty > 0 
        ? `${totalQty}/${totalRequiredQty} ${itemsLabel}`
        : `${totalQty} ${itemsLabel}`;
    }

    const { format, currency } = this.#moneyConfig();
    if (this.refs.summaryTotal) {
      this.refs.summaryTotal.textContent = formatMoney(totalPrice, format, currency);
      this.refs.summaryTotal.style.display = totalPrice <= 0 ? 'none' : '';
    }

    const allComplete = total > 0 && completeSteps >= total;
    if (this.refs.cta instanceof HTMLButtonElement) {
      if (this.isPackInCart) {
        this.refs.cta.disabled = true;
        this.refs.cta.classList.add('d2x-pack-summary__cta--disabled');
        this.refs.cta.textContent = allComplete
          ? (labels.inBag || 'Pack in Shopping Bag')
          : (totalRequiredQty > 0 
              ? `${totalQty}/${totalRequiredQty} ${labels.selected || 'SELECTED'}`
              : `${totalQty} ${labels.selected || 'SELECTED'}`);
      } else {
        this.refs.cta.disabled = !allComplete;
        this.refs.cta.classList.toggle('d2x-pack-summary__cta--disabled', !allComplete);
        this.refs.cta.textContent = allComplete
          ? labels.addToCart || 'Add pack to cart'
          : (totalRequiredQty > 0 
              ? `${totalQty}/${totalRequiredQty} ${labels.selected || 'SELECTED'}`
              : `${totalQty} ${labels.selected || 'SELECTED'}`);
      }
    }
  };

  /**
   * @param {{title: string, mode?: string, requiredQty: number, dollarCap: number}} step
   * @param {PackLine[]} lines
   */
  #renderSummaryRow(step, lines) {
    const row = this.summaryRows.find((r) => r.dataset.productStep === step.title);
    if (!row) return;

    const isDollarLimit = step.mode === 'dollar_limit';
    const qty = lines.reduce((sum, l) => sum + l.qty, 0);
    const amount = lines.reduce((sum, l) => sum + l.price * l.qty, 0);
    const { format, currency } = this.#moneyConfig();

    const countEl = row.querySelector('.d2x-pack-summary__row-count');
    if (countEl) {
      countEl.textContent = isDollarLimit
        ? `(${formatMoney(amount, format, currency)}/${formatMoney(step.dollarCap, format, currency)})`
        : `(${qty}/${step.requiredQty})`;
    }

    const emptyButton = row.querySelector('.d2x-pack-summary__row-empty');
    if (emptyButton instanceof HTMLElement) {
      const hasItems = lines.length > 0;
      emptyButton.hidden = hasItems;
      emptyButton.style.display = hasItems ? 'none' : '';
    }

    const container = row.querySelector('.d2x-pack-summary__lines');
    const template = this.refs.lineTemplate;
    if (!container || !(template instanceof HTMLTemplateElement)) return;

    container.innerHTML = '';

    for (const line of lines) {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const root = fragment.querySelector('[data-line]');
      if (!(root instanceof HTMLElement)) continue;

      const img = root.querySelector('img');
      if (img instanceof HTMLImageElement && line.image) {
        img.src = line.image;
        img.alt = line.title;
      }

      const titleEl = root.querySelector('[data-line-title]');
      if (titleEl) titleEl.textContent = line.title;

      const variantEl = root.querySelector('[data-line-variant]');
      if (variantEl instanceof HTMLElement) {
        const titleLower = (line.variantTitle || '').toLowerCase().trim();
        const hasVariant = Boolean(line.variantTitle && titleLower !== 'default title' && !titleLower.includes('default title'));
        variantEl.textContent = hasVariant ? line.variantTitle : '';
        variantEl.hidden = !hasVariant;
      }

      const qtyEl = root.querySelector('[data-qty]');
      if (qtyEl) qtyEl.textContent = String(line.qty);

      const otherQty = lines.filter((l) => l !== line).reduce((sum, l) => sum + l.qty, 0);
      const otherAmount = lines.filter((l) => l !== line).reduce((sum, l) => sum + l.price * l.qty, 0);
      const maxInventory = this.#getMaxInventory(line.variantId, line);
      const isStockLimitReached = maxInventory !== null && line.qty >= maxInventory;

      // Once a pack's lines are merged by a backend Cart Transform Function into one
      // bundle parent, the line-level `key` this component needs to call /cart/change.js
      // is no longer available (cart.items only exposes the merged wrapper) — these
      // rows are then restored from localStorage for display only. Disable qty/remove
      // rather than leave controls that look clickable but silently do nothing.
      const canAdjustInCart = !this.isPackInCart || Boolean(line.key);

      const decreaseBtn = root.querySelector('[data-decrease]');
      const increaseBtn = root.querySelector('[data-increase]');
      const removeBtn = root.querySelector('[data-remove]');
      if (decreaseBtn instanceof HTMLButtonElement) {
        decreaseBtn.disabled = this.isPackInCart ? !canAdjustInCart : line.qty <= 1;
        decreaseBtn.addEventListener('click', () => this.#changeQty(step.title, line.variantId, -1));
      }
      if (increaseBtn instanceof HTMLButtonElement) {
        increaseBtn.disabled = this.isPackInCart ? true : (isDollarLimit
          ? otherAmount + line.price * (line.qty + 1) > step.dollarCap
          : otherQty + line.qty >= step.requiredQty);
        increaseBtn.addEventListener('click', () => this.#changeQty(step.title, line.variantId, 1));
      }
      if (removeBtn instanceof HTMLButtonElement) {
        removeBtn.disabled = this.isPackInCart && !canAdjustInCart;
        removeBtn.addEventListener('click', () => this.#removeLine(step.title, line.variantId));
      } else {
        removeBtn?.addEventListener('click', () => this.#removeLine(step.title, line.variantId));
      }

      const errorEl = root.querySelector('[data-line-error]');
      const errorMsg = this.#lineErrors.get(line.variantId);
      if (errorEl instanceof HTMLElement) {
        if (errorMsg) {
          errorEl.textContent = errorMsg;
          errorEl.removeAttribute('hidden');
          errorEl.style.display = 'block';
        } else {
          errorEl.textContent = '';
          errorEl.setAttribute('hidden', '');
          errorEl.style.display = 'none';
        }
      }

      container.appendChild(fragment);
    }
  }

  #moneyConfig() {
    return {
      format: this.dataset.moneyFormat || '${{amount}}',
      currency: this.dataset.currency || 'USD',
    };
  }

  #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  /** @param {Event} event */
  #submit = async (event) => {
    event.preventDefault();
    if (!(this.refs.cta instanceof HTMLButtonElement) || this.refs.cta.disabled) return;

    /** @type {PackLine[]} */
    const allLines = [...this.#selections.values()].flat();
    if (!allLines.length) return;

    const labels = this.#readLabels();
    const originalText = this.refs.cta.textContent;
    this.refs.cta.disabled = true;
    this.refs.cta.textContent = labels.adding || 'Adding…';

    const payload = {
      items: allLines.map((line) => ({
        id: Number(line.variantId),
        quantity: line.qty,
        properties: { _pack_id: this.packHandle, _pack_step: line.step },
      })),
      sections: this.#cartSectionIds(),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((r) => r.json());

      if (response.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, response.message, response.description, response.errors));
        this.#restoreCta(labels.error, originalText);
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`)
        .then((r) => r.json())
        .catch(() => null);

      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, this.id, {
          source: 'd2x-pack-builder',
          itemCount: payload.items.length,
          sections: response.sections,
        })
      );

      if (cart) {
        // Selections stay in localStorage after submit (cleared only once the pack is
        // actually removed from the cart — see #syncWithCart) so the summary panel can
        // still show what was picked if the customer navigates back to this page and a
        // backend Cart Transform Function has merged the pack's lines into one bundle.
        this.#syncWithCart(cart);
      } else {
        this.#reset();
      }
    } catch (error) {
      console.error('D2X pack add to cart failed:', error);
      this.#restoreCta(labels.error, originalText);
    }
  };

  /**
   * @param {string} [text]
   * @param {string | null} [fallback]
   */
  #restoreCta(text, fallback) {
    if (!(this.refs.cta instanceof HTMLButtonElement)) return;
    this.refs.cta.disabled = false;
    this.refs.cta.textContent = text || fallback || this.refs.cta.textContent;
    setTimeout(this.#refresh, 2500);
  }

  #reset() {
    this.#lineErrors.clear();
    for (const step of this.stepConfig) {
      this.#selections.set(step.title, []);
    }
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      /* no-op */
    }
    document.dispatchEvent(new CustomEvent(Events.reset));
    this.#visitedSteps.clear();
    for (const step of this.stepConfig) {
      this.#showPage(step.title, 0);
    }
    if (this.tabs[0]) this.showStep(this.tabs[0].dataset.productStep || '');
    this.#refresh();
    this.#positionDrawer();
  }

  /** Expands/collapses the summary drawer on mobile (the whole header is the tap target). */
  #toggleSummary = () => {
    if (window.innerWidth >= DRAWER_BREAKPOINT) return;
    this.refs.summary?.classList.toggle('d2x-pack-summary--collapsed');
    this.#updateOverlay();
  };

  #closeSummary = () => {
    if (window.innerWidth >= DRAWER_BREAKPOINT) return;
    this.refs.summary?.classList.add('d2x-pack-summary--collapsed');
    this.#updateOverlay();
  };

  #positionDrawer = () => {
    if (!this.refs.summary) return;
    if (window.innerWidth >= DRAWER_BREAKPOINT) {
      this.refs.summary.classList.remove('d2x-pack-summary--collapsed');
      this.#updateOverlay();
      return;
    }
    this.refs.summary.classList.add('d2x-pack-summary--collapsed');
    this.#updateOverlay();
  };

  #updateOverlay() {
    const isCollapsed = this.refs.summary?.classList.contains('d2x-pack-summary--collapsed');
    const isMobile = window.innerWidth < DRAWER_BREAKPOINT;
    const showOverlay = isMobile && !isCollapsed;
    if (this.refs.summaryOverlay) {
      this.refs.summaryOverlay.classList.toggle('active', showOverlay);
    }
  }

  #onCartUpdate = (event) => {
    const cart = event.detail?.resource;
    if (cart && Array.isArray(cart.items)) {
      this.#syncWithCart(cart);
    } else {
      this.#syncCartAsync();
    }
  };

  async #syncCartAsync() {
    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      this.#syncWithCart(cart);
    } catch (error) {
      console.error('Failed to sync pack builder with cart:', error);
    }
  }

  /**
   * @param {Object} cart
   */
  #syncWithCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    const packItems = cart.items.filter(
      (item) => item.properties && item.properties._pack_id === this.packHandle
    );

    if (packItems.length > 0) {
      this.isPackInCart = true;

      // Rebuild the summary from the cart's own line items when they're still exposed
      // per step (`_pack_step` present on each line). A backend Cart Transform Function
      // can merge this pack's lines into a single bundle parent for $0 pricing — once
      // that happens, `cart.items` only contains that one merged line (no `_pack_step`),
      // so there's nothing to rebuild from. In that case, keep showing the last-known
      // selections from localStorage instead of wiping the summary to empty.
      /** @type {Map<string, PackLine[]>} */
      const rebuilt = new Map();
      for (const step of this.stepConfig) {
        rebuilt.set(step.title, []);
      }

      let matchedStepLines = false;
      for (const item of packItems) {
        const stepName = item.properties._pack_step;
        if (!stepName || !rebuilt.has(stepName)) continue;

        matchedStepLines = true;
        rebuilt.get(stepName).push({
          variantId: String(item.id),
          key: item.key,
          productId: item.product_id,
          step: stepName,
          title: item.product_title || item.title,
          variantTitle: item.variant_title || '',
          image: item.image,
          price: item.price,
          qty: item.quantity,
        });
      }

      if (matchedStepLines) {
        this.#selections = rebuilt;
      } else {
        this.#restore();
      }
    } else {
      if (this.isPackInCart) {
        this.isPackInCart = false;
        for (const step of this.stepConfig) {
          this.#selections.set(step.title, []);
        }
        try {
          localStorage.removeItem(this.storageKey);
        } catch {}
        document.dispatchEvent(new CustomEvent(Events.reset));
        this.#visitedSteps.clear();
        for (const step of this.stepConfig) {
          this.#showPage(step.title, 0);
        }
        if (this.tabs[0]) this.showStep(this.tabs[0].dataset.productStep || '');
      } else {
        this.#restore();
      }
    }

    this.#refresh();
  }

  /**
   * @param {string} key
   * @param {number} qty
   */
  async #updateCartItem(key, qty) {
    if (!key) return;

    const labels = this.#readLabels();
    let originalText = '';
    if (this.refs.cta instanceof HTMLButtonElement) {
      originalText = this.refs.cta.textContent || '';
      this.refs.cta.disabled = true;
      this.refs.cta.textContent = labels.adding || 'Updating…';
    }

    const body = JSON.stringify({
      id: key,
      quantity: qty,
      sections: this.#cartSectionIds(),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      }).then((r) => r.json());

      if (response.status && response.status !== 200) {
        console.error('Cart update error:', response.message);
        this.#restoreCta(labels.error, originalText);
        return;
      }

      this.#syncWithCart(response);

      const newCartItemCount = response.item_count || 0;
      this.dispatchEvent(
        new CartUpdateEvent(response, this.id, {
          itemCount: newCartItemCount,
          source: 'd2x-pack-builder',
          sections: response.sections,
          preventAutoOpen: true,
        })
      );
    } catch (error) {
      console.error('Cart update failed:', error);
      this.#restoreCta(labels.error, originalText);
    }
  }

  /**
   * @param {Object} detail
   */
  async #addCartItem(detail) {
    const labels = this.#readLabels();
    let originalText = '';
    if (this.refs.cta instanceof HTMLButtonElement) {
      originalText = this.refs.cta.textContent || '';
      this.refs.cta.disabled = true;
      this.refs.cta.textContent = labels.adding || 'Adding…';
    }

    const body = JSON.stringify({
      items: [{
        id: Number(detail.variantId),
        quantity: 1,
        properties: { _pack_id: this.packHandle, _pack_step: detail.step },
      }],
      sections: this.#cartSectionIds(),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      }).then((r) => r.json());

      if (response.status && response.status !== 200) {
        console.error('Cart add error:', response.message);
        this.#restoreCta(labels.error, originalText);
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      this.#syncWithCart(cart);

      this.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          itemCount: cart.item_count,
          source: 'd2x-pack-builder',
          sections: response.sections,
          preventAutoOpen: true,
        })
      );
    } catch (error) {
      console.error('Cart add failed:', error);
      this.#restoreCta(labels.error, originalText);
    }
  }
}

if (!customElements.get('d2x-pack-builder-component')) {
  customElements.define('d2x-pack-builder-component', D2xPackBuilder);
}
